import React from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiEye,
  FiLayout,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";

function ThSort({ label, col, sort, dir, onSort }) {
  const active = sort === col;
  const arrow = !active ? "" : dir === "ASC" ? "↑" : "↓";

  return (
    <th
      className="cursor-pointer select-none px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400"
      onClick={() => onSort(col)}
      title="Trier"
    >
      {label}{" "}
      {arrow ? <span className="text-slate-400">{arrow}</span> : null}
    </th>
  );
}

function ChipActive({ value }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${
        value
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
          : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200"
      }`}
    >
      {value ? <FiToggleRight /> : <FiToggleLeft />}
      {value ? "Actif" : "Inactif"}
    </span>
  );
}

export default function PageTable({
  loading,
  items,
  parentsMap,
  page,
  total,
  totalPages,
  onPrev,
  onNext,
  onEdit,
  onToggle,
  onDelete,
  onPreview,
  onContent,
  sort,
  dir,
  onSort,
  fmtDate,
}) {
  return (
    <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900 dark:text-slate-50">
            Liste des pages
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {loading ? "Chargement…" : `${total} résultat(s) trouvé(s)`}
          </div>
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          onPrev={onPrev}
          onNext={onNext}
        />
      </div>

      {loading ? (
        <div className="grid gap-3 p-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400 dark:bg-slate-800">
            <FiLayout className="text-2xl" />
          </div>

          <h3 className="mt-4 text-lg font-bold">Aucune page trouvée</h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Modifiez vos filtres ou créez une nouvelle page.
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50">
                  <ThSort
                    label="Titre"
                    col="title"
                    sort={sort}
                    dir={dir}
                    onSort={onSort}
                  />
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Parent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Statut
                  </th>
                  <ThSort
                    label="Créée"
                    col="created_at"
                    sort={sort}
                    dir={dir}
                    onSort={onSort}
                  />
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {items.map((p) => {
                  const parent = p.parent_id
                    ? parentsMap.get(p.parent_id)
                    : null;

                  return (
                    <tr
                      key={p.id}
                      className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                    >
                      <td className="px-4 py-4">
                        <div className="font-bold text-slate-950 dark:text-slate-50">
                          {p.title}
                        </div>

                        {p.subtitle ? (
                          <div className="mt-1 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
                            {p.subtitle}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-4 py-4">
                        <code className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          /pages/{p.slug}
                        </code>
                      </td>

                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                        {parent ? (
                          parent.title
                        ) : (
                          <span className="text-slate-400">Racine</span>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <ChipActive value={!!p.is_active} />
                      </td>

                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                        {fmtDate(p.created_at)}
                      </td>

                      <td className="px-4 py-4">
                        <Actions
                          page={p}
                          onPreview={onPreview}
                          onEdit={onEdit}
                          onContent={onContent}
                          onToggle={onToggle}
                          onDelete={onDelete}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 p-4 md:hidden">
            {items.map((p) => {
              const parent = p.parent_id ? parentsMap.get(p.parent_id) : null;

              return (
                <article
                  key={p.id}
                  className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-slate-950 dark:text-slate-50">
                        {p.title}
                      </h3>

                      <code className="mt-2 inline-block rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        /pages/{p.slug}
                      </code>
                    </div>

                    <ChipActive value={!!p.is_active} />
                  </div>

                  {p.subtitle ? (
                    <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                      {p.subtitle}
                    </p>
                  ) : null}

                  <div className="mt-3 grid gap-1 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      Parent :{" "}
                      <strong className="text-slate-700 dark:text-slate-200">
                        {parent ? parent.title : "Racine"}
                      </strong>
                    </span>
                    <span>
                      Créée :{" "}
                      <strong className="text-slate-700 dark:text-slate-200">
                        {fmtDate(p.created_at)}
                      </strong>
                    </span>
                  </div>

                  <div className="mt-4">
                    <Actions
                      page={p}
                      onPreview={onPreview}
                      onEdit={onEdit}
                      onContent={onContent}
                      onToggle={onToggle}
                      onDelete={onDelete}
                      mobile
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      <div className="border-t border-slate-200 px-4 py-4 dark:border-slate-800">
        <Pagination
          page={page}
          totalPages={totalPages}
          onPrev={onPrev}
          onNext={onNext}
          align="end"
        />
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, onPrev, onNext, align = "between" }) {
  return (
    <div
      className={`flex items-center gap-2 ${
        align === "end" ? "justify-end" : "justify-between md:justify-end"
      }`}
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={page <= 1}
        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
        aria-label="Page précédente"
      >
        <FiChevronLeft />
      </button>

      <span className="rounded-2xl bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        {page} / {totalPages}
      </span>

      <button
        type="button"
        onClick={onNext}
        disabled={page >= totalPages}
        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
        aria-label="Page suivante"
      >
        <FiChevronRight />
      </button>
    </div>
  );
}

function Actions({
  page,
  onPreview,
  onEdit,
  onContent,
  onToggle,
  onDelete,
  mobile = false,
}) {
  const buttonBase =
    "inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition";

  return (
    <div
      className={`flex ${
        mobile ? "flex-wrap" : "items-center justify-end"
      } gap-2`}
    >
      <button
        type="button"
        onClick={() => onPreview(page.id)}
        className={`${buttonBase} border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800`}
      >
        <FiEye />
        Voir
      </button>

      <button
        type="button"
        onClick={() => onEdit(page.id)}
        className={`${buttonBase} border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800`}
      >
        <FiEdit2 />
        Infos
      </button>

      <button
        type="button"
        onClick={() => onContent(page.id)}
        className={`${buttonBase} border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200`}
      >
        <FiLayout />
        Contenu
      </button>

      <button
        type="button"
        onClick={() => onToggle(page.id)}
        className={`${buttonBase} border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800`}
      >
        {page.is_active ? <FiToggleLeft /> : <FiToggleRight />}
        {page.is_active ? "Désactiver" : "Activer"}
      </button>

      <button
        type="button"
        onClick={() => onDelete(page)}
        className={`${buttonBase} border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200`}
      >
        <FiTrash2 />
        Supprimer
      </button>
    </div>
  );
}